import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import GearTable from "../components/GearTable";
import EditableField from "../components/EditableField";
import { deleteCaseIfNoKits } from "../utils/caseHelpers";
import { duplicateCase } from "../utils/caseHelpers";
import toast from "react-hot-toast";
import CaseQRCardButton from "../components/CaseQRCardButton";

export default function CaseDetail() {
	const { id } = useParams();
	const { role, user } = useAuth();
	const [caseData, setCaseData] = useState(null);
	const [gearList, setGearList] = useState([]);
	const [gearTypes, setGearTypes] = useState([]);
	const [gearStatuses, setGearStatuses] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [associatedKits, setAssociatedKits] = useState([]);
	const [allKits, setAllKits] = useState([]);
	const [loadingKits, setLoadingKits] = useState(true);
	const [kitError, setKitError] = useState(null);
	const fileInputRef = useRef(null);
	const [uploadingImage, setUploadingImage] = useState(false);
	const [gearGroups, setGearGroups] = useState([]);
	const [selectedGear, setSelectedGear] = useState([]);
	const [allCases, setAllCases] = useState([]);
	const [showImageModal, setShowImageModal] = useState(false);
	const [showPackingView, setShowPackingView] = useState(false); // New state to toggle view

	const navigate = useNavigate();

	useEffect(() => {
		const fetchCaseData = async () => {
			try {
				const { data: caseItem, error: caseError } = await supabase
					.from("cases")
					.select("*")
					.eq("id", id)
					.single();

				if (caseError) throw caseError;
				setCaseData(caseItem);
			} catch (err) {
				setError("Failed to fetch case data");
				console.error(err);
			} finally {
				setLoading(false);
			}
		};

		fetchCaseData();
	}, [id]);

	useEffect(() => {
		async function loadGearData() {
			try {
				const { gearGroups, gearItems } = await fetchCaseGearAndGroups(
					id
				);
				setGearGroups(gearGroups);
				setGearList(gearItems);
			} catch (err) {
				setError("Failed to load gear data");
				console.error(err);
			} finally {
				setLoading(false);
			}
		}

		loadGearData();
	}, [id]);

	useEffect(() => {
		async function fetchGearTypesAndStatuses() {
			try {
				const [
					{ data: types, error: typesError },
					{ data: statuses, error: statusesError },
				] = await Promise.all([
					supabase.from("gear_types").select("*"),
					supabase.from("gear_statuses").select("*"),
				]);

				if (typesError) throw typesError;
				if (statusesError) throw statusesError;

				setGearTypes(types);
				setGearStatuses(statuses);
			} catch (error) {
				console.error("Failed to fetch gear types or statuses:", error);
				setError("Failed to load gear types or statuses");
			}
		}

		fetchGearTypesAndStatuses();
	}, []);

	useEffect(() => {
		async function fetchCases() {
			const { data, error } = await supabase
				.from("cases")
				.select("id, name");
			if (error) {
				console.error("Failed to fetch cases:", error);
			} else {
				setAllCases(data);
			}
		}
		fetchCases();
	}, []);

	const fetchAssociatedKits = useCallback(async () => {
		setLoadingKits(true);
		const { data, error } = await supabase
			.from("kit_cases")
			.select(
				`
      kits (
        id,
        name,
		color
      )
    `
			)
			.eq("case_id", id);

		if (error) {
			setKitError("Failed to load associated kits");
			console.error(error);
		} else {
			setAssociatedKits(data.map((kc) => kc.kits));
		}
		setLoadingKits(false);
	}, [id]);

	useEffect(() => {
		if (id) {
			fetchAssociatedKits();
		}
	}, [id, fetchAssociatedKits]);

	async function fetchAllKits() {
		const { data, error } = await supabase.from("kits").select("*");
		if (error) {
			console.error("Failed to load kits:", error);
		} else if (data) {
			setAllKits(data);
		}
	}

	useEffect(() => {
		fetchAllKits();
	}, []);

	async function addKitToCase(kitId) {
		const { error } = await supabase
			.from("kit_cases")
			.insert([{ kit_id: kitId, case_id: id }])
			.select();
		const toastId = toast.loading("Adding case...");
		if (error) {
			toast.error("Failed to add case to kit: " + error, { id: toastId });
			console.error(error);
		} else {
			toast.success("Case added to kit successfully.", { id: toastId });
			await fetchAssociatedKits(); // wait for fetchAssociatedKits to update state
		}
	}

	async function handleRemoveFromKit(kitId) {
		const { error } = await supabase
			.from("kit_cases")
			.delete()
			.eq("kit_id", kitId)
			.eq("case_id", id);
		const toastId = toast.loading("Removing case...");
		if (error) {
			toast.error("Failed to remove case from kit: " + error, {
				id: toastId,
			});
			console.error(error);
			return;
		}
		toast.success("Case removed from kit successfully.", { id: toastId });
		fetchAssociatedKits();
	}

	async function updateCaseField(field, value) {
		const { error } = await supabase
			.from("cases")
			.update({ [field]: value })
			.eq("id", id);

		if (error) {
			alert("Failed to update case: " + error.message);
			return;
		}
		setCaseData((prev) => ({ ...prev, [field]: value }));
	}

	async function handleImageUpload(event) {
		setUploadingImage(true);
		const file = event.target.files[0];
		if (!file) {
			setUploadingImage(false);
			return;
		}

		const fileExt = file.name.split(".").pop();
		const fileName = `case-${id}-${Date.now()}.${fileExt}`;
		const filePath = `${fileName}`; // assuming `case-images` is the bucket, not the folder

		const { error: uploadError } = await supabase.storage
			.from("case-images")
			.upload(filePath, file, {
				cacheControl: "3600",
				upsert: true,
			});
		const toastId = toast.loading("Uploading image...");
		if (uploadError) {
			toast.error("Image upload failed: " + uploadError.message, {
				id: toastId,
			});
			console.error(uploadError);
			setUploadingImage(false);
			return;
		}

		const {
			data: { publicUrl },
			error: urlError,
		} = supabase.storage.from("case-images").getPublicUrl(filePath);

		if (urlError) {
			toast.error("Failed to get public url", {
				id: toastId,
			});
			console.error(urlError);
			setUploadingImage(false);
			return;
		}
		toast.success("Image uploaded successfully.", { id: toastId });
		await updateCaseField("image_url", publicUrl);
		setUploadingImage(false);
	}

	async function addGear({ name, group_name = null }) {
		const { data, error } = await supabase
			.from("gear")
			.insert([
				{
					name,
					case_id: id,
					quantity: 1,
					status_id: 1,
					group_name,
				},
			])
			.select();

		if (error) {
			throw new Error("Failed to add gear: " + error.message);
		}
		if (!data || data.length === 0) {
			throw new Error("Failed to add gear: no data returned");
		}
		return data[0];
	}

	async function updateGearField(id, field, value) {
		const { error } = await supabase
			.from("gear")
			.update({ [field]: value })
			.eq("id", id);

		if (error) throw new Error(error.message);
	}

	async function removeGear(id) {
		const { error } = await supabase.from("gear").delete().eq("id", id);
		if (error) throw error;
		return true;
	}

	async function handleDeleteCase() {
		const result = await deleteCaseIfNoKits(id, { dryRun: true });

		if (!result.success) {
			toast.error(result.message);
			return;
		}

		if (!window.confirm("Are you sure you want to delete this case?"))
			return;

		const toastId = toast.loading("Deleting case...");
		const deleteResult = await deleteCaseIfNoKits(id);

		if (!deleteResult.success) {
			toast.error("Failed to delete case: " + deleteResult.message, {
				id: toastId,
			});
		} else {
			toast.success("Case deleted successfully.", { id: toastId });
			navigate("/");
		}
	}

	async function handleDeleteImage(url) {
		// Extract just the file path after /case-images/
		const pathParts = url.split("/case-images/");
		if (pathParts.length !== 2) return alert("Invalid image URL");

		const filePath = pathParts[1];

		const { error: deleteError } = await supabase.storage
			.from("case-images")
			.remove([`case-images/${filePath}`]);
		const toastId = toast.loading("Deleting image...");
		if (deleteError) {
			toast.error("Failed to delete image from kit: " + deleteError, {
				id: toastId,
			});
			console.error(deleteError);
			return;
		}
		toast.success("Image deleted successfully.", { id: toastId });
		await updateCaseField("image_url", null);
	}

	async function fetchCaseGearAndGroups(id) {
		const [
			{ data: gearGroups, error: groupError },
			{ data: gearItems, error: gearError },
		] = await Promise.all([
			supabase
				.from("gear_groups")
				.select("*")
				.eq("case_id", id)
				.order("id", { ascending: true }),

			supabase
				.from("gear")
				.select("*")
				.eq("case_id", id)
				.order("order", { ascending: true }),
		]);

		if (groupError || gearError) {
			throw new Error(groupError?.message || gearError?.message);
		}

		return { gearGroups, gearItems };
	}

	async function createGearGroup(groupName) {
		const { data, error } = await supabase
			.from("gear_groups")
			.insert([{ case_id: id, group_name: groupName }])
			.select()
			.single();

		if (error) throw new Error(error.message);
		setGearGroups((prev) => [...prev, data]);
		return data;
	}

	useEffect(() => {
		function handleKeyDown(e) {
			if (e.key === "Escape") {
				setShowImageModal(false);
			}
		}

		if (showImageModal) {
			document.addEventListener("keydown", handleKeyDown);
		}

		return () => {
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [showImageModal]);

	async function handleDuplicateCase(caseId) {
		await duplicateCase(caseId, navigate);
	}

	// Simplified view toggle (Packing view)
	const handlePackingViewToggle = () => {
		setShowPackingView((prevState) => !prevState);
	};

	if (loading) {
		return (
			<div className="loading">
				<div>
					<i className="pi pi-spin pi-spinner"></i>
				</div>
			</div>
		);
	}
	if (error) return <div style={{ color: "red" }}>{error}</div>;
	if (!caseData) return <div>Case not found.</div>;

	// Render packing view
	const renderPackingView = () => (
		<div className="main pack-view">
			<h1>{caseData.name}</h1>
			{caseData.image_url && (
				<img
					src={caseData.image_url}
					alt={caseData.name}
					style={{
						maxWidth: "100px",
						display: "block",
						margin: "auto",
					}}
				/>
			)}

			<GearTable
				gearList={gearList}
				setGearList={setGearList}
				gearGroups={gearGroups}
				setGearGroups={setGearGroups}
				gearTypes={gearTypes}
				gearStatuses={gearStatuses}
				userRole={role}
				addGear={addGear}
				updateGearField={updateGearField}
				removeGear={removeGear}
				createGearGroup={createGearGroup}
				selectedGear={selectedGear}
				setSelectedGear={setSelectedGear}
				allCases={allCases}
				caseName={caseData.name}
				associatedKits={associatedKits}
			/>
		</div>
	);

	// Render the full detailed view
	const renderDetailedView = () => (
		<div className="main">
			<div className="case-detail__top">
				<div className="case-detail__top-left">
					<h1>
						{role === "admin" ? (
							<EditableField
								value={caseData.name}
								onSave={(val) => updateCaseField("name", val)}
								className="case-detail__name-edit"
								type="text"
							/>
						) : (
							caseData.name
						)}
					</h1>
					<div className="case-detail__weight">
						<strong>Weight:</strong>{" "}
						{role === "admin" ? (
							<>
								<EditableField
									value={
										(caseData.weight && caseData.weight) ||
										""
									}
									onSave={(val) => {
										const trimmed = val.trim();

										if (trimmed === "") {
											updateCaseField("weight", null); // Allow blank value
											return;
										}

										const numVal = parseFloat(trimmed);

										if (isNaN(numVal)) {
											alert("Weight must be a number");
											return;
										}

										updateCaseField("weight", numVal);
									}}
									className="case-weight"
									type="number"
								/>
							</>
						) : (
							<>&nbsp;{caseData.weight} lbs</>
						)}
					</div>

					<div className="case-detail__notes">
						<strong>Notes:</strong>
						<br />
						{user ? (
							<EditableField
								value={caseData.notes || ""}
								onSave={(val) => updateCaseField("notes", val)}
								className="case-notes"
								type="textarea"
							/>
						) : (
							<p style={{ whiteSpace: "pre-wrap" }}>
								{caseData.notes || "..."}
							</p>
						)}
					</div>

					<div className="case-detail__kits">
						<strong>Associated Kits</strong>
						<br />
						{loadingKits && <p>Loading kits...</p>}
						{kitError && <p style={{ color: "red" }}>{kitError}</p>}

						<div className="pill-wrapper">
							{associatedKits.length === 0 && (
								<div>This case does not belong to any kits</div>
							)}
							{associatedKits.map((kit) => (
								<span className="pill" key={kit.id}>
									{kit.color && (
										<span
											className="color"
											style={{
												backgroundColor: kit.color,
											}}
										></span>
									)}
									<Link
										to={`/kits/${kit.id}`}
										className="pill-link"
									>
										{kit.name}
									</Link>
									{role === "admin" && (
										<>
											<button
												onClick={() => {
													handleRemoveFromKit(kit.id);
												}}
												className="pill-delete-button"
											>
												<i className="pi pi-times"></i>
											</button>
										</>
									)}
								</span>
							))}
						</div>
						{role === "admin" && (
							<>
								<label htmlFor="kit-select-label">
									Add to Kit:
								</label>
								<select
									id="kit-select"
									onChange={async (e) => {
										const kitId = e.target.value;
										if (kitId) {
											await addKitToCase(kitId);
											e.target.value = ""; // reset select after adding
										}
									}}
									defaultValue=""
								>
									<option value="" disabled>
										Select a kit
									</option>
									{allKits
										.filter(
											(kit) =>
												!associatedKits.some(
													(ak) => ak.id === kit.id
												)
										)
										.map((kit) => (
											<option key={kit.id} value={kit.id}>
												{kit.name}
											</option>
										))}
								</select>
							</>
						)}
					</div>
				</div>

				<div className="case-detail__top-right">
					{uploadingImage && (
						<div
							style={{
								position: "fixed",
								top: 0,
								left: 0,
								width: "100%",
								height: "100%",
								backgroundColor: "rgba(0,0,0,0.5)",
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								zIndex: 999,
							}}
						>
							<div
								style={{
									width: "60px",
									height: "60px",
									border: "8px solid #f3f3f3",
									borderTop: "8px solid #3498db",
									borderRadius: "50%",
									animation: "spin 1s linear infinite",
								}}
							/>
						</div>
					)}

					{caseData.image_url ? (
						<div>
							<img
								src={caseData.image_url}
								alt={`${caseData.name}`}
								style={{
									display: "block",
									cursor: "pointer",
								}}
								onClick={() => setShowImageModal(true)}
							/>

							{showImageModal && (
								<div
									className="image-modal-overlay"
									onClick={() => setShowImageModal(false)}
								>
									<button
										className="image-modal-close"
										onClick={() => setShowImageModal(false)}
										aria-label="Close"
									>
										&times;
									</button>
									<img
										src={caseData.image_url}
										alt={`${caseData.name}`}
										className="image-modal-content"
									/>
								</div>
							)}

							{role === "admin" && (
								<div className="case-detail__image-actions">
									<button
										onClick={() =>
											fileInputRef.current?.click()
										}
										className="button"
									>
										<i className="pi pi-replay"></i>
										Replace Image
									</button>

									<button
										onClick={() => {
											handleDeleteImage(
												caseData.image_url
											);
										}}
										className="button red"
									>
										<i className="pi pi-trash"></i>
										Delete Image
									</button>
								</div>
							)}

							<input
								type="file"
								ref={fileInputRef}
								accept="image/*"
								style={{ display: "none" }}
								onChange={handleImageUpload}
							/>
						</div>
					) : (
						<>
							{role === "admin" && (
								<div className="case-detail__image-placeholder">
									<label htmlFor="image-upload">
										Upload Image:
									</label>
									<input
										type="file"
										id="image-upload"
										accept="image/*"
										onChange={handleImageUpload}
									/>
								</div>
							)}
						</>
					)}
				</div>
			</div>

			<GearTable
				gearList={gearList}
				setGearList={setGearList}
				gearGroups={gearGroups}
				setGearGroups={setGearGroups}
				gearTypes={gearTypes}
				gearStatuses={gearStatuses}
				userRole={role}
				user={user}
				addGear={addGear}
				updateGearField={updateGearField}
				removeGear={removeGear}
				createGearGroup={createGearGroup}
				selectedGear={selectedGear}
				setSelectedGear={setSelectedGear}
				allCases={allCases}
				caseName={caseData.name}
				associatedKits={associatedKits}
			/>

			{role === "admin" && (
				<div className="case-detail__bottom-actions-wrap">
					<CaseQRCardButton
						caseId={caseData.id}
						templateUrl={"/gear/card-1050x600.png"} // same-origin is simplest
						baseUrl={"https://berrymg.com/gear"} // pin prod domain if you want
						qrBox={{ x: 615, y: 10, size: 390 }} // tweak to your blank area
					/>
					<button
						onClick={() => {
							handleDuplicateCase(caseData.id);
						}}
						className="button"
					>
						<i className="pi pi-clone"></i>
						Duplicate Case
					</button>
					<button onClick={handleDeleteCase} className="button red">
						<i className="pi pi-trash"></i>
						Delete Case
					</button>
				</div>
			)}
		</div>
	);

	return (
		<div>
			<button
				onClick={handlePackingViewToggle}
				className="button case-detail__view-toggle-button"
			>
				{showPackingView ? (
					<>
						<i className="pi pi-arrow-up-right-and-arrow-down-left-from-center"></i>
						Detail View
					</>
				) : (
					<>
						<i className="pi pi-arrow-down-left-and-arrow-up-right-to-center"></i>
						Show Pack View
					</>
				)}
			</button>

			{showPackingView ? renderPackingView() : renderDetailedView()}
		</div>
	);
}
